import { Button, Card, StateChip } from "@herefor/design-system";
import { ONBOARDING_MESSAGES as COPY } from "@herefor/product-language";
import { useState } from "react";

import type { OnboardingArchetype } from "./onboardingData";

export interface OnboardingExperienceProps {
  readonly archetype: OnboardingArchetype;
  readonly organiserName: string | null;
  readonly privacyHref: string;
  readonly onPrimary: () => void;
  readonly onSkip: () => void;
}

/** One non-carousel orientation, with a client-only practice that cannot persist. */
export function OnboardingExperience({
  archetype,
  organiserName,
  privacyHref,
  onPrimary,
  onSkip,
}: OnboardingExperienceProps) {
  const [practising, setPractising] = useState(false);
  const [practiceCovered, setPracticeCovered] = useState(false);
  const copy = copyFor(archetype, organiserName);

  return (
    <section className="onboarding" data-onboarding-archetype={archetype}>
      <p className="onboarding__eyebrow">{COPY.eyebrow}</p>
      <h1>{copy.heading}</h1>
      <p className="onboarding__introduction">{copy.introduction}</p>

      <ul className="onboarding__model">
        <li>{COPY.mentalModel.conversation}</li>
        <li>{COPY.mentalModel.sourceOfTruth}</li>
        <li>{COPY.mentalModel.availability}</li>
      </ul>

      <div className="onboarding__actions">
        <Button onClick={onPrimary} variant="primary">
          {copy.primaryAction}
        </Button>
        <Button onClick={onSkip}>{COPY.skip}</Button>
      </div>

      <a className="onboarding__privacy" href={privacyHref}>
        {COPY.privacyLink}
      </a>

      {practising ? (
        <Card title={COPY.practice.heading} titleLevel={2}>
          <p>{COPY.practice.need}</p>
          <p>{COPY.practice.explanation}</p>
          <StateChip
            label={practiceCovered ? COPY.practice.covered : COPY.practice.state}
            state={practiceCovered ? "covered" : "needsSomeone"}
          />
          <div className="onboarding__actions">
            {practiceCovered ? null : (
              <Button
                onClick={() => {
                  setPracticeCovered(true);
                }}
                variant="secondary"
              >
                {COPY.practice.agreeAction}
              </Button>
            )}
            {practiceCovered ? <p>{COPY.practice.result}</p> : null}
            <Button
              onClick={() => {
                setPractising(false);
                setPracticeCovered(false);
              }}
            >
              {COPY.practice.finish}
            </Button>
          </div>
        </Card>
      ) : (
        <Button
          onClick={() => {
            setPractising(true);
          }}
        >
          {COPY.practice.action}
        </Button>
      )}
    </section>
  );
}

export function HelpSurface({ privacyHref }: { readonly privacyHref: string }) {
  return (
    <section className="onboarding-help">
      <h1>{COPY.help.heading}</h1>
      <p>{COPY.help.introduction}</p>
      <Card title={COPY.help.withdrawalTitle} titleLevel={2}>
        <p>{COPY.help.withdrawalBody}</p>
      </Card>
      <Card title={COPY.help.sharingTitle} titleLevel={2}>
        <p>{COPY.help.sharingBody}</p>
        <a href={privacyHref}>{COPY.privacyLink}</a>
      </Card>
      <Card title={COPY.help.emergencyTitle} titleLevel={2}>
        <p>{COPY.help.emergencyBody}</p>
      </Card>
    </section>
  );
}

function copyFor(archetype: OnboardingArchetype, organiserName: string | null) {
  if (archetype === "organiser") return COPY.organiser;
  if (archetype === "helper") {
    return { ...COPY.helper, heading: COPY.helper.heading(organiserName) };
  }
  return COPY.viewOnly;
}
